import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Alert,
  Divider,
  Grid,
  TextField,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PlayArrow,
  History,
  DeleteOutline,
  Close,
} from '@mui/icons-material';
import ApiServices from '../Services/ApiServices';
import { storageHelper } from '../../Helpers/storageHelper';
import { toastHelper } from '../../Helpers/toastHelper';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import CodeMirror from '@uiw/react-codemirror';
import { sql } from '@codemirror/lang-sql';
import DropdownBox from '../Shared/DropdownBox';

const SQL_COMMAND_TYPES = [
  { id: 1, name: 'SELECT', description: 'Query data' },
  { id: 2, name: 'DDL', description: 'Data Definition Language (CREATE, ALTER, DROP)' },
  { id: 3, name: 'DML', description: 'Data Manipulation Language (INSERT, UPDATE, DELETE)' },
];

const QueryExecutor = () => {
  const [query, setQuery] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [queryHistory, setQueryHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedCommandType, setSelectedCommandType] = useState(SQL_COMMAND_TYPES[0]);
  const [showPassword, setShowPassword] = useState(false);

  // Load saved credentials and history when component mounts
  useEffect(() => {
    loadSavedCredentials();
    loadQueryHistory();
  }, []);

  const loadSavedCredentials = () => {
    try {
      const savedUsername = storageHelper.getItem('queryExecutor_username');
      const savedPassword = storageHelper.getItem('queryExecutor_password');

      if (savedUsername) setUsername(savedUsername);
      if (savedPassword) setPassword(savedPassword);
    } catch (error) {
      
    }
  };

  const loadQueryHistory = () => {
    try {
      const savedHistory = storageHelper.getItem('queryExecutor_history');
      if (savedHistory) {
        setQueryHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      
    }
  };

  const saveCredentials = (username, password) => {
    try {
      storageHelper.setItem('queryExecutor_username', username);
      storageHelper.setItem('queryExecutor_password', password);
    } catch (error) {
      
    }
  };

  const saveQueryHistory = (history) => {
    try {
      storageHelper.setItem('queryExecutor_history', JSON.stringify(history));
    } catch (error) {
      
    }
  };

  const resetScreen = () => {
    setQuery('');
    setResults(null);
    setError(null);
    setShowResults(false);
    setSelectedCommandType(SQL_COMMAND_TYPES[0]);
  };

  const executeQuery = async () => {
    if (!query.trim()) {
      setError('Query cannot be empty');
      return;
    }

    if (!username.trim()) {
      setError('Username cannot be empty');
      return;
    }

    if (!password.trim()) {
      setError('Password cannot be empty');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await ApiServices.post('query/execute', {
        query: query.trim(),
        sql_command_type: selectedCommandType.id,
        user_name: username.trim(),
        password: password.trim(),
      });
      // Save credentials after successful execution
      saveCredentials(username.trim(), password.trim());

      // Add to history and save
      const newHistory = [
        {
          query: query.trim(),
          timestamp: new Date().toISOString(),
          type: selectedCommandType.name,
        },
        ...queryHistory,
      ];
      setQueryHistory(newHistory);
      saveQueryHistory(newHistory);

      if (selectedCommandType.id === 1) {
        // SELECT
        if (response.data && Array.isArray(response.data)) {
          setResults(response.data);
          setShowResults(true);
        } else {
          setError('Invalid response format from server');
        }
      } else {
        // DDL or DML
        const successMessage = response.data || `${selectedCommandType.name} query executed successfully`;
        toastHelper.success(successMessage);
        resetScreen();
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error || err?.error || err?.message || err || 'An error occurred while executing the query';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCommandTypeSelect = (commandType) => {
    setSelectedCommandType(commandType);
  };

  const handleHistorySelect = (item) => {
    setQuery(item.query);
    setSelectedCommandType(SQL_COMMAND_TYPES.find(ct => ct.name === item.type) || SQL_COMMAND_TYPES[0]);
    setShowHistory(false);
  };

  const clearHistory = () => {
    try {
      storageHelper.removeItem('queryExecutor_history');
      setQueryHistory([]);
    } catch (error) {
      setError('Failed to clear history');
    }
  };

  const renderResults = () => {
    if (!results || !Array.isArray(results)) return null;

    if (results.length === 0) {
      return (
        <Card sx={{ marginTop: 3, backgroundColor: '#fafafa' }}>
          <CardContent sx={{ textAlign: 'center', padding: 3 }}>
            <Typography variant="body1" color="textSecondary">
              No results found
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const columnKeys = Object.keys(results[0]);
    const columns = columnKeys.map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      sortable: true,
      align: 'center',
    }));

    return (
      <Card sx={{ marginTop: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="h6">Query Results</Typography>
            <Chip label={`${results.length} rows returned`} size="small" />
          </Box>
          <Divider sx={{ marginBottom: 2 }} />
          <SoftGrid
            columns={columns}
            data={results}
            isLoading={false}
            onSort={(columnKey, direction) => {
              
            }}
            onSearch={(searchTerm) => {
              
            }}
            emptyStateMessage="No results found"
            searchPlaceholder="Search in results..."
            useExportButtons={true}
            exportFilename={`query-results-${new Date().getTime()}`}
            isColumnSelectable={true}
          />
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ paddingY: 4 }}>
      <Paper sx={{ padding: 3, marginBottom: 3 }}>
        {/* Toolbar */}
        <Stack direction="row" gap={2} sx={{ marginBottom: 3 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrow />}
            onClick={executeQuery}
            disabled={loading}
            sx={{ flex: 1 }}
          >
            {loading ? <CircularProgress size={24} sx={{ marginRight: 1 }} /> : null}
            Execute
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<History />}
            onClick={() => setShowHistory(true)}
            sx={{ flex: 1 }}
          >
            History
          </Button>
        </Stack>

        {/* Credentials Section */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
            Database Credentials
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Command Type Selection */}
        <DropdownBox
          label="Command Type"
          options={SQL_COMMAND_TYPES}
          selectedOption={selectedCommandType}
          onSelect={handleCommandTypeSelect}
          title="Select Command Type"
          required={true}
          fullWidth={true}
        />

        {/* Query Input */}
        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
            SQL Query
          </Typography>
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundColor: '#1e1e1e',
              '& .cm-editor': {
                height: '300px',
                fontFamily: 'monospace',
              },
              '& .cm-gutters': {
                backgroundColor: '#252526',
                borderRight: '1px solid #3e3e42',
              },
              '& .cm-lineNumber': {
                color: '#858585',
                minWidth: '50px',
              },
            }}
          >
            <CodeMirror
              value={query}
              height="300px"
              extensions={[sql()]}
              onChange={(value) => setQuery(value)}
              theme="dark"
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                foldGutter: true,
                dropCursor: true,
                allowMultipleSelections: true,
                indentOnInput: true,
              }}
              placeholder="Enter your SQL query here..."
              style={{
                fontSize: '14px',
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: 3 }} action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setError(null)}
          >
            <Close fontSize="small" />
          </IconButton>
        }>
          {error}
        </Alert>
      )}

      {/* Results Display */}
      {showResults && renderResults()}

      {/* History Dialog */}
      <Dialog open={showHistory} onClose={() => setShowHistory(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Query History</DialogTitle>
        <DialogContent dividers>
          {queryHistory.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', padding: 2 }}>
              No query history
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {queryHistory.map((item, index) => (
                <Card
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 },
                    padding: 2
                  }}
                  onClick={() => handleHistorySelect(item)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                    <Chip
                      label={item.type}
                      size="small"
                      color={item.type === 'SELECT' ? 'primary' : 'secondary'}
                      variant="filled"
                    />
                    <Typography variant="caption" color="textSecondary">
                      {new Date(item.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#333333',
                    }}
                  >
                    {item.query}
                  </Typography>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            startIcon={<DeleteOutline />}
            onClick={clearHistory}
          >
            Clear History
          </Button>
          <Button onClick={() => setShowHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QueryExecutor;
